import {
  DatabaseInstanceAgentV2Enum,
  DatabaseInstanceProxyV2Enum,
  DatabaseInstanceTypeV2Enum,
  DboCachingSummaryCacheModeEnum,
  DboCloudProviderEnum,
} from '@cast/types-database-optimizer';

import type {
  DboCloudDetails,
  DboDatabase,
  DboDatabaseInstanceV2,
  DboEndpointV2,
  DboFeaturesV2,
  DboRecommendationStats,
} from '@cast/types-database-optimizer';

/**
 * Flagship demo instances rendered in the onboarding "explore demos" table.
 * Marketing data, safe for the production bundle (no mock-middleware imports).
 * The mock store imports this module to serve the same instances over the
 * API so visiting a demo dashboard shows data consistent with the table.
 */

const endpoint = (demoId: string, deployed = true): DboEndpointV2 => {
  const base: DboEndpointV2 = {
    address: `${demoId}.db.internal:5432`,
    readonly: false,
  };
  if (!deployed) return base;
  const dboHostname = `db-proxy-${demoId}.castai-db-optimizer`;
  const dboPort = 5432;
  return {
    ...base,
    dboConnectionString: `postgres://${dboHostname}:${dboPort}`,
    dboHostname,
    dboPort,
  };
};

type DemoDatabaseStats = {
  openQueryRecommendationsCount: number;
  openIndexRecommendationsCount: number;
};

const advisorStats = (stats: DemoDatabaseStats): DboRecommendationStats => ({
  affectedQueriesCount: 15,
  affectedQueriesDbTimeMs: 25000,
  affectedQueriesDbTimePercentage: 35,
  analyzedIndexesCount: 120,
  analyzedTablesCount: 350,
  closedIndexRecommendationsCount: 8,
  closedQueryRecommendationsCount: 12,
  openIndexRecommendationsCount: stats.openIndexRecommendationsCount,
  openQueryRecommendationsCount: stats.openQueryRecommendationsCount,
  lastUpdateTime: '2026-09-05T10:00:00.000Z',
});

type DemoCachingStats = {
  cacheHits: string;
  /** Fraction 0-1 — the API contract; the UI multiplies by 100. */
  hitRate: number;
  dbTimeSavePercentage: number;
  projectedCacheHits: string;
  /** Fraction 0-1 — the API contract; the UI multiplies by 100. */
  projectedHitRate: number;
  projectedDbTimeSavePercentage: number;
  dqlQueries: string;
  totalQueries: string;
  totalTimeMs: string;
};

type CreateDatabaseParams = {
  name: string;
  id: string;
  stats: DemoDatabaseStats;
  caching: DemoCachingStats;
};

const createDatabase = ({
  caching,
  id,
  name,
  stats,
}: CreateDatabaseParams): DboDatabase => ({
  id,
  name,
  advisorStats: advisorStats(stats),
  cacheConfigId: `cache-config-${id}`,
  cachingSummary: {
    cacheMode: DboCachingSummaryCacheModeEnum.ON,
    ...caching,
  },
  connectionsSummary: {
    active: 45,
    idle: 12,
    incoming: 200,
    outgoing: 50,
    reduction: 65,
  },
});

const createUnoptimizedDatabase = (
  params: CreateDatabaseParams
): DboDatabase => ({
  ...createDatabase(params),
  // Not onboarded: no cache mode yet — the UI falls back to the projected
  // values from the caching stats.
  cachingSummary: {
    cacheMode: DboCachingSummaryCacheModeEnum.CACHE_MODE_UNSPECIFIED,
    ...params.caching,
  },
});

type DemoInstance = {
  id: string;
  name: string;
  protocol: 'POSTGRESQL' | 'MYSQL';
  cloud?: DboCloudDetails;
  databases: DboDatabase[];
  caching: DemoCachingStats;
  /** Percent 0-100 — rendered raw by the UI. */
  reduction: number;
};

const instance = (demo: DemoInstance): DboDatabaseInstanceV2 => ({
  id: demo.id,
  name: demo.name,
  cacheGroupId: demo.id,
  dbProxyId: `proxy-${demo.id}`,
  protocol: demo.protocol,
  type: DatabaseInstanceTypeV2Enum.STANDALONE,
  agentStatus: DatabaseInstanceAgentV2Enum.CONNECTED,
  proxyStatus: DatabaseInstanceProxyV2Enum.CONNECTED,
  agentDeploymentStatus: { installTime: '2025-07-20T00:00:00.000Z' },
  proxyDeploymentStatus: { installTime: '2025-07-23T00:00:00.000Z' },
  agentChartVersions: { installed: '0.28.0', available: '0.28.0' },
  proxyChartVersions: { installed: '0.34.0', available: '0.34.0' },
  createTime: '2025-06-01T00:00:00.000Z',
  endpoints: [endpoint(demo.id)],
  cloudDetails: demo.cloud,
  features: {
    advisor: true,
    caching: true,
    pooling: true,
    cachingUpdateTime: '2025-07-23T00:00:00.000Z',
    poolingUpdateTime: '2025-07-24T00:00:00.000Z',
  },
  databases: demo.databases,
  cachingSummary: {
    cacheMode: DboCachingSummaryCacheModeEnum.ON,
    ...demo.caching,
  },
  connectionsSummary: {
    active: 45,
    idle: 12,
    incoming: 210,
    outgoing: 58,
    reduction: demo.reduction,
    maxConnections: 500,
  },
  recommendationsSummary: {
    recommendationsTotal: demo.databases.reduce(
      (acc, db) =>
        acc +
        (db.advisorStats?.openQueryRecommendationsCount ?? 0) +
        (db.advisorStats?.openIndexRecommendationsCount ?? 0),
      0
    ),
    affectedQueriesCount: 15,
    affectedQueriesDbTimeMs: 25000,
  },
});

/**
 * Cloud-discovered instances with no features enabled — served only while
 * demo mode is active, so the "Not onboarded" tab has content.
 */
const unoptimizedInstance = (demo: DemoInstance): DboDatabaseInstanceV2 => ({
  ...instance(demo),
  dbProxyId: undefined,
  agentStatus: DatabaseInstanceAgentV2Enum.STATUS_UNSPECIFIED,
  proxyStatus: DatabaseInstanceProxyV2Enum.STATUS_UNSPECIFIED,
  agentDeploymentStatus: undefined,
  proxyDeploymentStatus: undefined,
  // Not deployed: upstream endpoint without DBO proxy fields, and no
  // installed chart versions to report.
  endpoints: [endpoint(demo.id, false)],
  agentChartVersions: undefined,
  proxyChartVersions: undefined,
  features: {
    advisor: false,
    caching: false,
    pooling: false,
  } satisfies DboFeaturesV2,
  cachingSummary: {
    cacheMode: DboCachingSummaryCacheModeEnum.CACHE_MODE_UNSPECIFIED,
    ...demo.caching,
  },
  connectionsSummary: {
    active: 180,
    idle: 95,
    incoming: 420,
    outgoing: 110,
    reduction: 0,
    maxConnections: 500,
  },
  recommendationsSummary: undefined,
});

const MANUAL_CACHING: DemoCachingStats = {
  cacheHits: '482130',
  hitRate: 0.684,
  dbTimeSavePercentage: 45.2,
  projectedCacheHits: '579000',
  projectedHitRate: 0.821,
  projectedDbTimeSavePercentage: 57.9,
  dqlQueries: '612400',
  totalQueries: '705300',
  totalTimeMs: '1240800',
};

const AWS_CACHING: DemoCachingStats = {
  cacheHits: '721450',
  hitRate: 0.718,
  dbTimeSavePercentage: 48.6,
  projectedCacheHits: '848100',
  projectedHitRate: 0.843,
  projectedDbTimeSavePercentage: 61.2,
  dqlQueries: '894200',
  totalQueries: '1005600',
  totalTimeMs: '2183400',
};

const GCP_CACHING: DemoCachingStats = {
  cacheHits: '356780',
  hitRate: 0.642,
  dbTimeSavePercentage: 42.7,
  projectedCacheHits: '441900',
  projectedHitRate: 0.795,
  projectedDbTimeSavePercentage: 55.4,
  dqlQueries: '520400',
  totalQueries: '623800',
  totalTimeMs: '987200',
};

const DEMO_MANUAL_INSTANCE: DboDatabaseInstanceV2 = instance({
  id: 'demo-manual-postgres',
  name: 'Demo Manual PostgreSQL',
  protocol: 'POSTGRESQL',
  databases: [
    createDatabase({
      name: 'Production',
      id: 'demo-manual-postgres-production',
      stats: {
        openQueryRecommendationsCount: 6,
        openIndexRecommendationsCount: 3,
      },
      caching: MANUAL_CACHING,
    }),
    createDatabase({
      name: 'Analytics',
      id: 'demo-manual-postgres-analytics',
      stats: {
        openQueryRecommendationsCount: 2,
        openIndexRecommendationsCount: 1,
      },
      caching: MANUAL_CACHING,
    }),
  ],
  caching: MANUAL_CACHING,
  reduction: 72,
});

const DEMO_AWS_INSTANCE: DboDatabaseInstanceV2 = instance({
  id: 'demo-aws-postgres',
  name: 'Demo AWS RDS PostgreSQL',
  protocol: 'POSTGRESQL',
  cloud: {
    provider: DboCloudProviderEnum.AWS,
    cloudAccountId: 'acc-demo-aws-001',
    dbEngine: 'PostgreSQL',
    engineVersion: '16.4',
    averageConnectionsCount: 85,
    cpuUsagePercentage: 42,
    maxConnectionsLastHourCount: 150,
    memoryUsagePercentage: 58,
    instanceClass: 'db.r6g.2xlarge',
    region: 'us-east-1',
    storageSizeGb: 500,
    storageType: 'gp3',
    queriesLastHourCount: 12000,
  },
  databases: [
    createDatabase({
      name: 'Production',
      id: 'demo-aws-postgres-production',
      stats: {
        openQueryRecommendationsCount: 8,
        openIndexRecommendationsCount: 4,
      },
      caching: AWS_CACHING,
    }),
    createDatabase({
      name: 'Analytics',
      id: 'demo-aws-postgres-analytics',
      stats: {
        openQueryRecommendationsCount: 3,
        openIndexRecommendationsCount: 1,
      },
      caching: AWS_CACHING,
    }),
  ],
  caching: AWS_CACHING,
  reduction: 68,
});

const DEMO_GCP_INSTANCE: DboDatabaseInstanceV2 = instance({
  id: 'demo-gcp-postgres',
  name: 'Demo Cloud SQL PostgreSQL',
  protocol: 'POSTGRESQL',
  cloud: {
    provider: DboCloudProviderEnum.GCP,
    cloudAccountId: 'acc-demo-gcp-001',
    dbEngine: 'PostgreSQL',
    engineVersion: '16.4',
    averageConnectionsCount: 78,
    cpuUsagePercentage: 38,
    maxConnectionsLastHourCount: 140,
    memoryUsagePercentage: 54,
    instanceClass: 'db-custom-8-32',
    region: 'us-central1',
    storageSizeGb: 400,
    storageType: 'SSD',
    queriesLastHourCount: 10500,
  },
  databases: [
    createDatabase({
      name: 'Production',
      id: 'demo-gcp-postgres-production',
      stats: {
        openQueryRecommendationsCount: 5,
        openIndexRecommendationsCount: 2,
      },
      caching: GCP_CACHING,
    }),
    createDatabase({
      name: 'Analytics',
      id: 'demo-gcp-postgres-analytics',
      stats: {
        openQueryRecommendationsCount: 1,
        openIndexRecommendationsCount: 1,
      },
      caching: GCP_CACHING,
    }),
  ],
  caching: GCP_CACHING,
  reduction: 75,
});

const UNOPTIMIZED_CACHING: DemoCachingStats = {
  cacheHits: '0',
  hitRate: 0,
  dbTimeSavePercentage: 0,
  projectedCacheHits: '920400',
  projectedHitRate: 0.88,
  projectedDbTimeSavePercentage: 64.5,
  dqlQueries: '1046000',
  totalQueries: '1180000',
  totalTimeMs: '2870400',
};

const DEMO_DISCOVERED_POSTGRES_INSTANCE: DboDatabaseInstanceV2 =
  unoptimizedInstance({
    id: 'demo-discovered-postgres',
    name: 'Production Orders DB',
    protocol: 'POSTGRESQL',
    cloud: {
      provider: DboCloudProviderEnum.AWS,
      cloudAccountId: 'acc-demo-aws-001',
      dbEngine: 'PostgreSQL',
      engineVersion: '15.7',
      averageConnectionsCount: 210,
      cpuUsagePercentage: 78,
      maxConnectionsLastHourCount: 340,
      memoryUsagePercentage: 82,
      instanceClass: 'db.r6g.xlarge',
      region: 'us-west-2',
      storageSizeGb: 500,
      storageType: 'gp3',
      queriesLastHourCount: 48000,
    },
    databases: [
      createUnoptimizedDatabase({
        name: 'Orders',
        id: 'demo-discovered-postgres-orders',
        stats: {
          openQueryRecommendationsCount: 9,
          openIndexRecommendationsCount: 5,
        },
        caching: UNOPTIMIZED_CACHING,
      }),
    ],
    caching: UNOPTIMIZED_CACHING,
    reduction: 0,
  });

const DEMO_DISCOVERED_MYSQL_INSTANCE: DboDatabaseInstanceV2 =
  unoptimizedInstance({
    id: 'demo-discovered-mysql',
    name: 'Analytics MySQL',
    protocol: 'MYSQL',
    cloud: {
      provider: DboCloudProviderEnum.GCP,
      cloudAccountId: 'acc-demo-gcp-001',
      dbEngine: 'MySQL',
      engineVersion: '8.0',
      averageConnectionsCount: 145,
      cpuUsagePercentage: 71,
      maxConnectionsLastHourCount: 260,
      memoryUsagePercentage: 76,
      instanceClass: 'db-custom-8-32',
      region: 'europe-west1',
      storageSizeGb: 750,
      storageType: 'SSD',
      queriesLastHourCount: 32000,
    },
    databases: [
      createUnoptimizedDatabase({
        name: 'Analytics',
        id: 'demo-discovered-mysql-analytics',
        stats: {
          openQueryRecommendationsCount: 6,
          openIndexRecommendationsCount: 3,
        },
        caching: UNOPTIMIZED_CACHING,
      }),
    ],
    caching: UNOPTIMIZED_CACHING,
    reduction: 0,
  });

export const DEMO_INSTANCES: DboDatabaseInstanceV2[] = [
  DEMO_MANUAL_INSTANCE,
  DEMO_AWS_INSTANCE,
  DEMO_GCP_INSTANCE,
];

export const DEMO_INSTANCE_IDS: string[] = DEMO_INSTANCES.map((i) => i.id!);

/**
 * Discovered (unoptimized) demo instances — only served by the list
 * endpoint while demo mode is active.
 */
export const DEMO_UNOPTIMIZED_INSTANCES: DboDatabaseInstanceV2[] = [
  DEMO_DISCOVERED_POSTGRES_INSTANCE,
  DEMO_DISCOVERED_MYSQL_INSTANCE,
];

/**
 * Every entity id the demo dataset exposes — instance ids (doubled as cache
 * group ids), proxy ids (`proxy-<instanceId>`), database ids and cache config
 * ids (`cache-config-<databaseId>`).
 *
 * The mock middleware matches request params against this set to serve demo
 * data without the Dev Toolbox override, so every id must be derivable
 * statically — no faker, no store imports.
 */
export const DEMO_ENTITY_IDS: ReadonlySet<string> = new Set(
  [...DEMO_INSTANCES, ...DEMO_UNOPTIMIZED_INSTANCES].flatMap((instance) => [
    instance.id!,
    ...(instance.dbProxyId ? [instance.dbProxyId] : []),
    ...(instance.databases ?? []).flatMap((db) => [db.id!, db.cacheConfigId!]),
  ])
);
